import { IconArrowUpRight, IconBolt, IconBrandZapier, IconChevronDown, IconKey, IconPlus } from "@tabler/icons-react"
import { type FC } from "react"

import { toDateTimeString } from "@forento/core/utilities/date"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import { DeleteIcon } from "~/components/Icon"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

type Item = {
	name: string
	description: string
	inputs?: { key: string; label: string }[]
	outputs?: { key: string; label: string }[]
}
const documentation: { actions: Item[]; triggers: Item[] } = {
	actions: [
		{
			name: "Join Membership",
			description:
				"Assigns a user to a membership tier without initiating the subscription through the platform.",
			inputs: [
				{
					key: "membershipId",
					label: 'The Forento membership ID, shown as "Zapier ID" on each membership on the memberships page.',
				},
				{
					key: "userId",
					label: 'The Forento user. Pick a user from the dropdown, or map the user ID from a trigger or the "Get or Create User by E-Mail" action.',
				},
			],
			outputs: [
				{ key: "membershipId", label: "The Forento membership ID." },
				{ key: "userId", label: "The Forento user ID." },
			],
		},
		{
			name: "Cancel Membership",
			description: "Cancels a user's membership initiated through Zapier.",
			inputs: [
				{
					key: "userId",
					label: 'The Forento user. Pick a user from the dropdown, or map the user ID from a trigger or the "Get or Create User by E-Mail" action.',
				},
				{
					key: "date",
					label: "The date and time the membership should end. If not provided, the membership will end immediately.",
				},
			],
			outputs: [
				{ key: "userId", label: "The Forento user ID." },
				{ key: "endDate", label: "The date and time the membership ends." },
			],
		},
		{
			name: "Give User Access to Product",
			description:
				"Gives user access to a product without purchasing it through Forento. This could be a course, video, downloadable, session, or event.",
			inputs: [
				{
					key: "productType",
					label: "The type of product to give access to. Can be 'course', 'video', 'downloadable', 'session', or 'event'.",
				},
				{
					key: "productId",
					label: 'The Forento product ID, shown as "Zapier ID" on the product\'s edit page. It must belong to a product of the selected type.',
				},
				{
					key: "userId",
					label: 'The Forento user. Pick a user from the dropdown, or map the user ID from a trigger or the "Get or Create User by E-Mail" action.',
				},
			],
			outputs: [
				{
					key: "productType",
					label: "The type of product the user has access to. Can be 'course', 'video', 'downloadable', 'session', or 'event'.",
				},
				{ key: "productId", label: "The Forento product ID." },
				{ key: "userId", label: "The Forento user ID." },
			],
		},
		{
			name: "Remove User Access to Product",
			description:
				"Removes user access to a product added through Zapier. This could be a course, video, downloadable, session, or event.",
			inputs: [
				{
					key: "productType",
					label: "The type of product to remove access from. Can be 'course', 'video', 'downloadable', 'session', or 'event'.",
				},
				{
					key: "productId",
					label: 'The Forento product ID, shown as "Zapier ID" on the product\'s edit page. It must belong to a product of the selected type.',
				},
				{
					key: "userId",
					label: 'The Forento user. Pick a user from the dropdown, or map the user ID from a trigger or the "Get or Create User by E-Mail" action.',
				},
			],
			outputs: [
				{
					key: "productType",
					label: "The type of product the user no longer has access to. Can be 'course', 'video', 'downloadable', 'session', or 'event'.",
				},
				{ key: "productId", label: "The Forento product ID." },
				{ key: "userId", label: "The Forento user ID." },
			],
		},
		{
			name: "Get or Create User by E-Mail",
			description:
				"Finds the user with the e-mail address, or creates them if none exists. A new user will receive an e-mail to complete its profile.",
			inputs: [{ key: "email", label: "The e-mail address of the user." }],
			outputs: [
				{ key: "id", label: "The Forento user ID." },
				{ key: "email", label: "The e-mail address of the user." },
				{ key: "phoneNumber", label: "The phone number of the user." },
				{ key: "firstName", label: "The first name of the user." },
				{ key: "lastName", label: "The last name of the user." },
				{ key: "registerDate", label: "The date and time the user signed up." },
			],
		},
	],
	triggers: [
		{
			name: "User Signs Up",
			description: "Triggers when a user signs up to your platform.",
			outputs: [
				{ key: "id", label: "The Forento user ID" },
				{ key: "email", label: "The e-mail address of the user" },
				{ key: "firstName", label: "The first name of the user" },
				{ key: "lastName", label: "The last name of the user" },
				{ key: "registerDate", label: "The date and time the user signed up" },
			],
		},
		{
			name: "New Form Submission",
			description:
				"Triggers when someone submits a contact or newsletter form on your website. Only submissions made after this trigger was released are included.",
			outputs: [
				{ key: "id", label: "The Forento form submission ID" },
				{ key: "kind", label: "The type of form. Can be 'newsletter' or 'inquiry'" },
				{ key: "email", label: "The e-mail address that was submitted" },
				{ key: "name", label: "The name that was submitted, if the form asked for one" },
				{ key: "message", label: "The message that was submitted, if the form asked for one" },
				{ key: "pagePath", label: "The path of the website page the form is on" },
				{ key: "pageTitle", label: "The title of the website page the form is on" },
				{ key: "formKey", label: "The ID of the form, to tell several forms apart" },
				{ key: "createDate", label: "The date and time the form was submitted" },
			],
		},
	],
}

const ZapierSettingsPage = withAccessRequirement(
	"zapier",
	<PageBreadcrumb path={[{ title: "Settings", link: routes.settings.index() }]} title="Zapier" />,
	() => {
		const alert = useAlert()

		const { data, error, refetch } = query.zapier.listKeys.useQuery()

		const createKey = query.zapier.createKey.useMutation({
			onSuccess: async key => {
				await alert.show(
					"New API key",
					`Your new API key has been created. Please copy it now as it will not be shown again. Your API key:\n\n\`${key.apiKey}\``,
				)
				await refetch()
			},
			onError: () => alert.show("Error", "Failed to create an API key. Please try again."),
		})

		const deleteKey = query.zapier.deleteKey.useMutation({
			onSuccess: () => refetch(),
			onError: () => alert.show("Error", "Failed to delete key. Please try again later."),
		})

		async function handleDelete(id: number) {
			const dialog = await alert.confirm(
				"Delete API key",
				"Are you sure you want to delete this API key? It will stop working if any integrations are using it.",
			)
			if (!dialog.result) return
			await deleteKey.mutateAsync(id)
			dialog.close()
		}

		return (
			<Layout>
				<PageHeader>
					<PageBreadcrumb title="Zapier" path={[{ title: "Settings", link: routes.settings.index() }]} />
				</PageHeader>

				<div className="space-y-8 pb-8">
					<section className="border-component-border overflow-hidden rounded-3xl border bg-white">
						<div className="flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
							<div className="flex items-start gap-4">
								<div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
									<IconBrandZapier className="size-8" aria-hidden="true" />
								</div>
								<div className="max-w-xl">
									<h2 className="text-2xl font-semibold tracking-tight">
										Connect your tools. Automate your academy.
									</h2>
									<p className="mt-2 text-sm leading-relaxed text-gray-600">
										Send new sign-ups and form submissions to your other tools, or automatically
										give users access to products and memberships with Zapier.
									</p>
								</div>
							</div>
							<Button
								variant="secondary"
								newTab
								onClick="https://zapier.com/developer/public-invite/194814/cbc09863bf79b916fb8b30b0a18ccd39/"
								className="shrink-0 self-start lg:self-center"
							>
								Install Zapier app <IconArrowUpRight className="size-4" aria-hidden="true" />
							</Button>
						</div>
						<ol className="border-component-border bg-component-background grid gap-6 border-t p-6 sm:p-8 md:grid-cols-3">
							{[
								["Install the app", "Add Forento to your Zapier account using the link above."],
								["Create an API key", "Generate a key below and copy it while it’s visible."],
								["Connect in Zapier", "Paste your key into Zapier, then choose a trigger or action."],
							].map(([title, description], index) => (
								<li key={title} className="flex gap-3">
									<span className="border-component-border flex size-7 shrink-0 items-center justify-center rounded-full border bg-white text-xs font-semibold">
										{index + 1}
									</span>
									<div>
										<h3 className="text-sm font-semibold">{title}</h3>
										<p className="mt-1 text-sm leading-relaxed text-gray-500">{description}</p>
									</div>
								</li>
							))}
						</ol>
					</section>

					<section aria-labelledby="api-keys-heading" className="space-y-4">
						<div className="flex flex-wrap items-center justify-between gap-4">
							<div>
								<h2 id="api-keys-heading" className="text-lg font-semibold">
									API keys
								</h2>
								<p className="mt-1 text-sm text-gray-500">
									Keys give Zapier access to your academy. Keep them private.
								</p>
							</div>
							<Button
								variant="primary"
								isLoading={createKey.isPending}
								onClick={() => createKey.mutate()}
							>
								<IconPlus className="size-4" aria-hidden="true" /> Create API key
							</Button>
						</div>
						{error ? (
							<div
								role="alert"
								className="border-danger/20 bg-danger/5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-5"
							>
								<p className="text-sm">Failed to load API keys.</p>
								<Button
									variant="secondary"
									onClick={() => {
										void refetch()
									}}
								>
									Try again
								</Button>
							</div>
						) : !data ? (
							<PartialLoadingPage />
						) : data.length === 0 ? (
							<div className="border-component-border flex flex-col items-center rounded-3xl border bg-white px-6 py-10 text-center">
								<div className="bg-component-background mb-4 rounded-2xl p-3">
									<IconKey className="size-6 text-gray-500" aria-hidden="true" />
								</div>
								<h3 className="font-semibold">Your first connection starts here</h3>
								<p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
									Create an API key to connect your academy to Zapier and start building automations.
								</p>
							</div>
						) : (
							<Table
								columns={["Created", "Last used", { label: "Actions", flex: { grow: 0, basis: 80 } }]}
								data={data.map(item => [
									<div className="flex items-center gap-3">
										<IconKey className="size-4 shrink-0 text-gray-400" aria-hidden="true" />
										{toDateTimeString(item.createDate)}
									</div>,
									item.lastUsedDate ? (
										toDateTimeString(item.lastUsedDate)
									) : (
										<span className="text-gray-500">Not used yet</span>
									),
									<div className="flex">
										<Tooltip tooltip="Delete API key">
											<button
												type="button"
												aria-label={`Delete API key created ${toDateTimeString(item.createDate)}`}
												disabled={deleteKey.isPending}
												onClick={() => handleDelete(item.id)}
												className="hover:bg-danger/5 hover:text-danger focus-visible:outline-primary cursor-pointer rounded-lg p-2 text-gray-400 transition-colors focus-visible:outline-2 disabled:opacity-50"
											>
												<DeleteIcon className="size-5" />
											</button>
										</Tooltip>
									</div>,
								])}
							/>
						)}
					</section>

					<section aria-labelledby="automations-heading" className="border-component-border border-t pt-8">
						<h2 id="automations-heading" className="text-lg font-semibold">
							Build your automation
						</h2>
						<p className="mt-1 text-sm text-gray-500">
							Start with a trigger, then choose what happens next. Expand an event to see its fields.
						</p>
						<div className="mt-6 grid items-start gap-6 lg:grid-cols-2">
							{[
								{
									title: "Triggers",
									description: "When something happens in Forento…",
									items: documentation.triggers,
								},
								{
									title: "Actions",
									description: "…do something in your academy.",
									items: documentation.actions,
								},
							].map(group => (
								<div key={group.title} className="min-w-0">
									<div className="mb-4 flex items-center gap-3">
										<IconBolt className="size-5 text-orange-600" aria-hidden="true" />
										<div>
											<h3 className="text-sm font-semibold">
												{group.title}{" "}
												<span className="ml-1 text-gray-400">{group.items.length}</span>
											</h3>
											<p className="mt-0.5 text-xs text-gray-500">{group.description}</p>
										</div>
									</div>
									<div className="divide-component-border border-component-border divide-y overflow-hidden rounded-2xl border bg-white">
										{group.items.map(item => (
											<Item key={item.name} item={item} />
										))}
									</div>
								</div>
							))}
						</div>
					</section>
				</div>
			</Layout>
		)
	},
)

const Item: FC<{ item: Item }> = ({ item }) => (
	<details className="group">
		<summary className="hover:bg-component-background focus-visible:outline-primary flex cursor-pointer list-none items-start gap-4 p-5 transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] [&::-webkit-details-marker]:hidden">
			<div className="min-w-0 flex-1">
				<h4 className="text-sm font-semibold">{item.name}</h4>
				<p className="mt-1.5 text-sm leading-relaxed text-gray-500">{item.description}</p>
			</div>
			<IconChevronDown
				className="mt-0.5 size-4 shrink-0 text-gray-400 group-open:rotate-180"
				aria-hidden="true"
			/>
		</summary>
		<div className="border-component-border bg-component-background/60 space-y-5 border-t p-5">
			{[
				{ title: "Input fields", fields: item.inputs },
				{ title: "Output fields", fields: item.outputs },
			].map(
				({ title, fields }) =>
					fields && (
						<div key={title}>
							<h5 className="mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
								{title}
							</h5>
							<dl className="space-y-3">
								{fields.map(field => (
									<div key={field.key}>
										<dt className="font-mono text-xs break-all text-gray-800">{field.key}</dt>
										<dd className="mt-1 text-sm leading-relaxed text-gray-500">{field.label}</dd>
									</div>
								))}
							</dl>
						</div>
					),
			)}
		</div>
	</details>
)

export default ZapierSettingsPage
